import { queryOptions, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { getIsLoggedIn } from '../../auth/utils/getIsLoggedIn'
import { communityApi } from '../api/communityApi'
import type { TMediumCommunityApi } from '../api/communityApi.types'
import { COMMUNITY_LIFETIME } from '../models/community.constants'
import { getComputedSubscription } from '../utils/getComputedSubscription'
import { useCommunityActiveSubscription } from './useCommunityActiveSubscription'

type TCommunitySubsctiptionOptionsCommunity = Pick<TMediumCommunityApi, 'id' | 'lifetime'>

export const getQueryCommunitySubscriptionOptions = (community?: TCommunitySubsctiptionOptionsCommunity) =>
  queryOptions({
    queryKey: ['community_subscription', community?.id],
    queryFn: ({ signal }) =>
      community &&
      communityApi.getSubscriptions(
        {
          'community.id': community.id,
          ...(community.lifetime === COMMUNITY_LIFETIME.EVERLASTING && {
            'community.publications.limit': 1,
            'community.publications.order': { effectivePublicationDate: 'desc' },
          }),
        },
        { signal },
      ),
    select: (data) => data?.member,
    enabled: !!community && getIsLoggedIn(),
  })

export const useCommunitySubscriptionsQuery = (communityId: number, lifetime?: COMMUNITY_LIFETIME) => {
  const query = useQuery(getQueryCommunitySubscriptionOptions(lifetime && { id: communityId, lifetime }))

  const rawActiveSubscription = query.data?.find(
    ({ toDate, leftAt }) => !leftAt && (!toDate || dayjs().subtract(10, 'days').isBefore(toDate, 'day')),
  )
  const activeSubscription =
    useCommunityActiveSubscription(communityId) ||
    (rawActiveSubscription && getComputedSubscription(rawActiveSubscription))
  const placeholderData = activeSubscription && [activeSubscription]

  return {
    activeSubscription,
    ...query,
    data: query.data || placeholderData,
  }
}
